import type { WikiReviewItem } from "./wiki.ts";
import {
  isFolderOp,
  type WikiChangeItem,
  type WikiChangeOp,
  type WikiChangeSet,
  type WikiReviewQueue,
  type WikiTrustReason,
} from "./wiki-change-sets.ts";

/** The kinds a change set is summarised by (wiki-collaboration-ux D8): a folder is never a page. */
export type ChangeKind = "created" | "edits" | "renames" | "moves" | "deletions" | "folders";
export const CHANGE_KINDS = [
  "created",
  "edits",
  "renames",
  "moves",
  "deletions",
  "folders",
] as const satisfies readonly ChangeKind[];

/** The kind an operation counts under. */
export function changeKindOf(op: WikiChangeOp): ChangeKind {
  if (isFolderOp(op)) {
    return "folders";
  }
  switch (op) {
    case "create":
      return "created";
    case "edit":
      return "edits";
    case "rename":
      return "renames";
    case "move":
      return "moves";
    default:
      return "deletions";
  }
}

/** A change set's sizes: counts per kind, and the edits' lines added and removed. */
export interface ChangeSizes {
  counts: Record<ChangeKind, number>;
  /** Lines added and removed over the edits that carry `stats`. */
  added: number;
  removed: number;
  /** Every edit carried `stats` (an N-1 API, or a diff too large, leaves some unmeasured). */
  measured: boolean;
  /** The number of items: the sum of the kinds. */
  items: number;
}

/**
 * The one count vocabulary (spec `project-wiki` "Change sizes by kind"): the card, the inbox,
 * Closed, the summary and the confirmation all read it. Empty kinds stay 0; callers omit them.
 */
export function changeSizes(set: {
  items: readonly Pick<WikiChangeItem, "op" | "stats">[];
}): ChangeSizes {
  const counts: Record<ChangeKind, number> = {
    created: 0,
    edits: 0,
    renames: 0,
    moves: 0,
    deletions: 0,
    folders: 0,
  };
  let added = 0;
  let removed = 0;
  let measured = true;
  for (const item of set.items) {
    const kind = changeKindOf(item.op);
    counts[kind]++;
    if (kind === "edits") {
      if (item.stats) {
        added += item.stats.added;
        removed += item.stats.removed;
      } else {
        measured = false;
      }
    }
  }
  return { counts, added, removed, measured, items: set.items.length };
}

/** A new page's length in lines (its Markdown as proposed), for the card and the outline. */
export function markdownLines(markdown: string | null | undefined): number {
  const text = (markdown ?? "").replace(/\r\n?/g, "\n").replace(/\n+$/, "");
  return text === "" ? 0 : text.split("\n").length;
}

/** What the viewer may do in the Changes inbox, from the capabilities the server computed. */
export interface ReviewViewer {
  userId: string;
  /**
   * Holds `wiki.publish` in a project that is not frozen. Not the decision itself (the set's
   * `trust`, computed by the server, is): a gate for a frozen project, and the fallback when an
   * N-1 API returns no `trust`.
   */
  canPublish: boolean;
  /** May resolve a review item of this kind. */
  canResolve: (kind: WikiReviewItem["kind"]) => boolean;
}

/** A change set row, with the review items attached to its items (contradictions). */
export interface ReviewSetRow {
  set: WikiChangeSet;
  flags: WikiReviewItem[];
  /**
   * Open threads that wait on the viewer (wiki-collaboration D16): change requests and questions
   * on their own proposal, questions on items they approved. Absent when none.
   */
  waiting?: { changeRequests: number; questions: number };
  /**
   * Why the viewer sees the row as they do (wiki-collaboration-ux D4): `waiting` when a thread
   * waits on them, else the set's `trust.reason`. Absent for the viewer's own sets and when an
   * N-1 API returns no reason.
   */
  reason?: WikiTrustReason;
}

/**
 * What open change requests block in `set` (wiki-collaboration D14), as core decides it: an
 * item's approval while one is open on it; publication while one is open on the set or on an
 * item that could still be published. The review screen and the side panel share it.
 */
export function changeRequestState(set: Pick<WikiChangeSet, "items" | "openThreads">) {
  const requested = new Set(
    (set.openThreads ?? []).filter((t) => t.kind === "change_request").map((t) => t.itemId),
  );
  return {
    /** An open change request on this item. */
    onItem: (itemId: string) => requested.has(itemId),
    publishBlocked:
      requested.has(null) ||
      set.items.some(
        (i) => requested.has(i.id) && (i.status === "pending" || i.status === "approved"),
      ),
  };
}

/**
 * Who may decide what (wiki-collaboration D9): the set as a whole, and each item, for the viewer.
 * At `peer`, an item the viewer last edited is someone else's to approve, and Approve all and
 * Publish are someone else's while one is still pending or approved. Absent trust (an older API):
 * nothing is refused here; the server decides.
 */
export function trustState(set: Pick<WikiChangeSet, "items" | "trust">) {
  const decides = set.trust?.mayDecide ?? true;
  const mine = new Set(set.trust?.editedByYou ?? []);
  const live = (status: string) => status === "pending" || status === "approved";
  return {
    decides,
    item: (itemId: string) => decides && !mine.has(itemId),
    /** Publish: no live item the viewer edited (Approve all skips those and says so). */
    whole: decides && !set.items.some((i) => mine.has(i.id) && live(i.status)),
    /** The viewer's own edits are what stops them. */
    ownEdits: decides && set.items.some((i) => mine.has(i.id) && live(i.status)),
  };
}

/** The open threads of `set` that wait on `me` (D16), or undefined. */
export function threadsWaitingOn(
  set: WikiChangeSet,
  me: string,
): ReviewSetRow["waiting"] | undefined {
  const mine = set.createdBy.toLowerCase() === me;
  const approved = new Set(
    set.items.filter((i) => i.approvedBy?.toLowerCase() === me).map((i) => i.id),
  );
  let changeRequests = 0;
  let questions = 0;
  for (const t of set.openThreads ?? []) {
    // A thread does not wait on the person who opened it.
    if (t.createdBy?.toLowerCase() === me) {
      continue;
    }
    if (t.kind === "change_request" && mine) {
      changeRequests++;
    } else if (t.kind === "question" && (mine || (t.itemId !== null && approved.has(t.itemId)))) {
      questions++;
    }
  }
  return changeRequests + questions > 0 ? { changeRequests, questions } : undefined;
}

export interface ReviewGroup {
  changeSets: ReviewSetRow[];
  /** Review items not attached to a listed change set (source changed, re-review). */
  items: WikiReviewItem[];
}

/** The Changes inbox's open views (spec `project-wiki` "Review queue groups rows by who acts"). */
export interface ChangesInbox {
  /** The sets the viewer may decide and the rows that wait on them: the only ones counted. */
  toDecide: ReviewGroup;
  /** The viewer's own sets, working ones included. */
  mine: ReviewGroup;
  /** The rest the viewer may see: sets they cannot act on, or commented on. */
  following: ReviewGroup;
  /** The review count, wherever it shows: To decide's rows. */
  count: number;
  /** The viewer the rows were grouped for: its `canResolve` gates each Resolve action. */
  viewer: ReviewViewer;
}

const empty = (): ReviewGroup => ({ changeSets: [], items: [] });

/**
 * The one predicate behind the review count and the Changes inbox (wiki-collaboration-ux D4,
 * spec `project-wiki` "Review count counts only rows the viewer must act on"). To decide holds a
 * colleague's submitted open set when the server-computed `trust` lets the viewer decide it as a
 * whole, or when a thread waits on them; never one of their own (`requester` lets a creator
 * decide, the inbox still lists it under Mine). A change item's contradictions ride in its set's
 * row and are never counted; detached review items are rows where the viewer may resolve them.
 */
export function changesInbox(
  queue: Pick<WikiReviewQueue, "items"> & { changeSets?: WikiChangeSet[] | undefined },
  viewer: ReviewViewer,
): ChangesInbox {
  const groups = { toDecide: empty(), mine: empty(), following: empty() };
  const me = viewer.userId.toLowerCase();
  const rowOf = new Map<string, ReviewSetRow>();
  for (const set of queue.changeSets ?? []) {
    const own = set.createdBy.toLowerCase() === me;
    // A working change set (wiki-collaboration D8) is listed for its creator only.
    if (set.submittedAt === null && !own) {
      continue;
    }
    const waiting = threadsWaitingOn(set, me);
    const row: ReviewSetRow = { set, flags: [], ...(waiting && { waiting }) };
    for (const item of set.items) {
      rowOf.set(item.id, row);
    }
    if (own) {
      groups.mine.changeSets.push(row);
      continue;
    }
    // Absent `trust` (an N-1 API): publish capability decides, as before this change.
    const decides =
      viewer.canPublish && set.status === "open" && (set.trust ? trustState(set).whole : true);
    const reason = waiting ? "waiting" : set.trust?.reason;
    if (reason) {
      row.reason = reason;
    }
    (decides || waiting ? groups.toDecide : groups.following).changeSets.push(row);
  }
  for (const item of queue.items) {
    const row =
      item.kind === "contradiction" && item.changeItemId ? rowOf.get(item.changeItemId) : undefined;
    if (row) {
      row.flags.push(item);
    } else {
      (viewer.canResolve(item.kind) ? groups.toDecide : groups.following).items.push(item);
    }
  }
  return {
    ...groups,
    count: groups.toDecide.changeSets.length + groups.toDecide.items.length,
    viewer,
  };
}
